/**
 * Query Persistence - IndexedDB storage for React Query cache
 * Allows React Query caches to survive app restarts for offline access
 */

import { QueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';

const DB_NAME = 'lupuslogix-query-cache';
const DB_VERSION = 1;
const STORE_NAME = 'queries';

interface StoredQuery {
  key: string;
  queryKey: unknown[];
  data: unknown;
  timestamp: number;
  userId: string;
}

/**
 * Initialize the IndexedDB database for query caching
 */
async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      logger.error('[QueryPersistence] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Generate a stable key from query key array
 */
function generateKey(queryKey: unknown[]): string {
  return JSON.stringify(queryKey);
}

/**
 * Hydrate React Query cache from IndexedDB on app startup
 * This allows offline access to previously fetched data
 */
export async function hydrateQueryCache(
  queryClient: QueryClient,
  userId: string | undefined
): Promise<void> {
  if (!userId) return;

  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');

    const entries = await new Promise<StoredQuery[]>((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    if (entries.length > 0) {
      logger.log(`[QueryPersistence] Hydrating ${entries.length} cached queries from IndexedDB`);
      
      for (const entry of entries) {
        // Only hydrate if no data exists in memory
        const existingData = queryClient.getQueryData(entry.queryKey as string[]);
        if (!existingData && entry.data) {
          queryClient.setQueryData(entry.queryKey as string[], entry.data);
          logger.log(`[QueryPersistence] Restored: ${entry.key.slice(0, 50)}...`);
        }
      }
    }
  } catch (error) {
    logger.error('[QueryPersistence] Failed to hydrate cache:', error);
  }
}

/**
 * Persist critical React Query caches to IndexedDB
 * Call this after fetching important data
 */
export async function persistQueryCache(
  queryClient: QueryClient,
  userId: string | undefined,
  queryKeys: unknown[][]
): Promise<void> {
  if (!userId) return;

  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const promises: Promise<void>[] = [];

    for (const queryKey of queryKeys) {
      const data = queryClient.getQueryData(queryKey);
      if (data) {
        // Skip persisting empty history-day entries to prevent stale "No data" cache
        const keyStr = generateKey(queryKey);
        const isHistoryDay = keyStr.includes('history-day') && !keyStr.includes('calendar-dates');
        if (isHistoryDay) {
          const historyData = data as { count?: number; entries?: unknown[] };
          // Only persist if there's actual data
          if (!historyData.count || historyData.count === 0) {
            logger.log(`[QueryPersistence] Skipping empty history-day: ${keyStr.slice(0, 60)}...`);
            continue;
          }
        }

        const entry: StoredQuery = {
          key: keyStr,
          queryKey,
          data,
          timestamp: Date.now(),
          userId,
        };

        promises.push(new Promise((resolve, reject) => {
          const request = store.put(entry);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));
      }
    }

    await Promise.all(promises);
    
    if (promises.length > 0) {
      logger.log(`[QueryPersistence] Persisted ${promises.length} queries to IndexedDB`);
    }
  } catch (error) {
    logger.error('[QueryPersistence] Failed to persist cache:', error);
  }
}

/**
 * Clear all cached queries for a user (e.g., on sign out)
 */
export async function clearQueryCache(userId: string): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');

    const entries = await new Promise<StoredQuery[]>((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    const promises = entries.map(entry => new Promise<void>((resolve, reject) => {
      const request = store.delete(entry.key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    }));

    await Promise.all(promises);
    logger.log(`[QueryPersistence] Cleared ${entries.length} cached queries for user`);
  } catch (error) {
    logger.error('[QueryPersistence] Failed to clear cache:', error);
  }
}

/**
 * List of critical query keys that should be persisted for offline access
 * These match the keys defined in useTabDataPrefetcher.ts and useHistoryDay.ts
 */
export const CRITICAL_QUERY_KEYS = {
  // Home Tab
  homeTab: (userId: string) => ['homeTab', 'healthData', userId],
  // Dashboard - profile (username)
  profile: (userId: string) => ['dashboard', 'profile', userId],
  // Dashboard - user settings (location, preferences)
  userSettings: (userId: string) => ['dashboard', 'user_settings', userId],
  // Meds Tab
  prescriptions: (userId: string) => ['medsTab', 'prescriptions', userId],
  // NOTE: actual hook key includes today's date BEFORE the userId
  // (see useMedsTabData.ts → TAKEN_MEDS_KEY = ['medsTab','takenMeds', today, userId]).
  // We persist using the same shape so the cache rehydrates correctly offline.
  takenMeds: (userId: string, dateISO: string) => ['medsTab', 'takenMeds', dateISO, userId],
  // Appointments Tab
  appointmentsList: (userId: string) => ['appointments', 'list', userId],
  upcomingAppointments: () => ['upcoming-appointments'],
  careTeamMembers: () => ['care-team-members'],
  appointmentsCount: () => ['appointments-count'],
  careTeamCount: () => ['care-team-count'],
  // Track Tab
  cyclePreference: (userId: string) => ['cycleTrackingPreference', userId],
  recentLogs: (userId: string) => ['trackTab', 'recentLogs', userId],
  // More Tab
  subscription: (userId: string) => ['subscription', userId],
  // Beta Tab
  betaStatus: (userId: string) => ['betaTab', 'status', userId],
  // Notification devices
  notificationDevices: (userId: string) => ['notification-devices', userId],
  // History - calendar dots (dates with data)
  historyCalendarDates: (userId: string) => ['history-calendar-dates', userId],
  // History - day entries (cache a week for offline access)
  historyDay: (userId: string, dateISO: string) => ['history-day', userId, dateISO],
};

/**
 * Get past N days as ISO date strings (YYYY-MM-DD)
 */
function getPastDays(days: number): string[] {
  const dates: string[] = [];
  const now = new Date();
  for (let i = 0; i < days; i++) {
    const date = new Date(now);
    date.setDate(now.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
}

/**
 * Get all critical query keys for a user
 */
export function getAllCriticalQueryKeys(userId: string): unknown[][] {
  // Cache past 7 days of history for offline access
  const pastWeek = getPastDays(7);
  const today = pastWeek[0];
  
  const baseKeys = [
    CRITICAL_QUERY_KEYS.homeTab(userId),
    CRITICAL_QUERY_KEYS.profile(userId),
    CRITICAL_QUERY_KEYS.userSettings(userId),
    CRITICAL_QUERY_KEYS.prescriptions(userId),
    CRITICAL_QUERY_KEYS.takenMeds(userId, today),
    CRITICAL_QUERY_KEYS.appointmentsList(userId),
    CRITICAL_QUERY_KEYS.upcomingAppointments(),
    CRITICAL_QUERY_KEYS.careTeamMembers(),
    CRITICAL_QUERY_KEYS.appointmentsCount(),
    CRITICAL_QUERY_KEYS.careTeamCount(),
    CRITICAL_QUERY_KEYS.cyclePreference(userId),
    CRITICAL_QUERY_KEYS.recentLogs(userId),
    CRITICAL_QUERY_KEYS.subscription(userId),
    CRITICAL_QUERY_KEYS.betaStatus(userId),
    CRITICAL_QUERY_KEYS.notificationDevices(userId),
    CRITICAL_QUERY_KEYS.historyCalendarDates(userId),
  ];
  
  // Add history day keys for past week
  const historyKeys = pastWeek.map(dateISO => 
    CRITICAL_QUERY_KEYS.historyDay(userId, dateISO)
  );
  
  return [...baseKeys, ...historyKeys];
}
